import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import type { TrendingDestination } from 'home/components/TrendingDestinations/constants'
import {
  getTrendingDestinationsCacheKey,
  getTrendingDestinationsForFilter,
  resolveTrendingDestinationUrl,
} from 'home/components/TrendingDestinations/constants'
import { fetchTrendingDestinations } from 'actions/DestinationAlertsActions'
import { buildHotelUrl, resolveTrendingDestinationTiles } from 'home/components/TrendingDestinations/utils'
import { EmptyArray } from 'lib/array/arrayUtils'
import { useLocalizedPlaces } from 'hooks/usePlace'

export function useHotelTrendingDestinations(
  selectedFilter: App.DestinationSelectedFilters,
  currentRegionCode: string,
  leUserId?: string,
  domainUserId?: string,
  disablePersonalisation?: boolean,
): [Array<TrendingDestination>, boolean] {
  const dispatch = useAppDispatch()
  const isHotelFilter = selectedFilter === 'hotel'

  const userKey = getTrendingDestinationsCacheKey(leUserId, domainUserId, disablePersonalisation)

  useEffect(() => {
    dispatch(fetchTrendingDestinations(leUserId, domainUserId, disablePersonalisation))
  }, [dispatch, leUserId, domainUserId, disablePersonalisation])

  const trendingDestinations = useAppSelector(
    state => state.destination?.trendingDestinationsByUserKey?.[userKey],
  ) ?? EmptyArray

  const fetching = useAppSelector(
    state => state.destination?.fetchingTrendingDestinationsByUserKey?.[userKey] ?? false,
  )

  const curatedTrending = useMemo(
    () => isHotelFilter ? [] : (getTrendingDestinationsForFilter(selectedFilter, currentRegionCode) ?? []),
    [isHotelFilter, selectedFilter, currentRegionCode],
  )

  const hotelTrending = useMemo(
    () => isHotelFilter ? trendingDestinations.filter(destination => destination.imageId) : [],
    [isHotelFilter, trendingDestinations],
  )

  const activeTrendingSource = isHotelFilter ? hotelTrending : curatedTrending

  const placeIds = useMemo(
    () => activeTrendingSource.map(destination => destination.placeId),
    [activeTrendingSource],
  )
  const localizedPlaces = useLocalizedPlaces(placeIds)

  const displayTrending: Array<TrendingDestination> = useMemo(() => {
    if (isHotelFilter) {
      if (fetching) return []

      return resolveTrendingDestinationTiles(
        hotelTrending,
        localizedPlaces,
        (destination, resolvedName) => resolvedName ? buildHotelUrl(resolvedName, destination.placeId) : undefined,
      )
    }

    return resolveTrendingDestinationTiles(
      curatedTrending,
      localizedPlaces,
      (destination, resolvedName) => resolveTrendingDestinationUrl(destination, resolvedName),
    )
  }, [isHotelFilter, fetching, hotelTrending, curatedTrending, localizedPlaces])

  const isCuratedLoading = !isHotelFilter && displayTrending.some(destination => destination.isLoading)

  return [displayTrending, isHotelFilter ? fetching : isCuratedLoading]
}
